<template>
  <div
    v-if="kitMetricsStore.isFetchingMetrics"
    class="e-flex e-items-center e-justify-center e-h-full"
  >
    <EvercamLoadingAnimation />
  </div>
  <div
    v-else
    class="kit-metrics e-flex e-flex-col e-items-center e-w-full e-h-full"
  >
    <div v-if="hasNoMetrics" class="e-flex e-justify-center">
      <span class="title font-wieght-medium red--text">
        No metrics available for this kit.
      </span>
    </div>
    <div
      v-for="(tableMetric, metricId) in tableMetrics"
      :key="'table-' + metricId"
      class="e-w-full"
    >
      <KitMetricCard
        :metric="tableMetric"
        :metric-id="metricId"
        :kit="kitSummaryStore.kit"
        class="e-m-3"
        @has-data="hasTableMetrics = $event"
      />
    </div>

    <KitMetricCard
      v-for="(timeSeriesMetric, metricId) in timeSeriesMetrics"
      :key="'series-' + metricId"
      class="e-m-3"
      :metric="timeSeriesMetric"
      :metric-id="metricId"
      :kit="kitSummaryStore.kit"
      :selected-period="kitMetricsStore.selectedMetricsPeriod"
      :is-loading="kitMetricsStore.isFetchingMetrics"
      @select-period="onMetricsPeriodChange"
      @has-data="hasTimeSeriesMetrics = $event"
    />
  </div>
</template>

<script lang="ts">
import Vue from "vue"
import { mapStores } from "pinia"
import { useKitSummaryStore } from "@/stores/kitSummary"
import { useKitMetricsStore } from "@/stores/kitMetrics"
import {
  EvercamMetricData,
  GrafanaMetricData,
  GrafanaMetricId,
  KitChartType,
  KitMetricId,
  KitMetricPeriod,
} from "@evercam/shared/types"

import KitMetricCard from "~/components/kits/KitMetricCard.vue"
import EvercamLoadingAnimation from "@evercam/shared/components/EvercamLoadingAnimation"

export default Vue.extend({
  name: "KitMetrics",
  components: { KitMetricCard, EvercamLoadingAnimation },
  data() {
    return {
      hasTimeSeriesMetrics: false,
      hasTableMetrics: false,
    }
  },
  computed: {
    ...mapStores(useKitSummaryStore, useKitMetricsStore),
    tableMetrics(): Array<Record<GrafanaMetricId, GrafanaMetricData>> {
      return this.kitMetricsStore.metricsByChartType[KitChartType.Table]
    },
    timeSeriesMetrics(): (
      | Record<GrafanaMetricId, GrafanaMetricData>
      | Record<KitMetricId, EvercamMetricData>
    )[] {
      const grafanaTimeSeries =
        this.kitMetricsStore.metricsByChartType[KitChartType.TimeSeries]

      return { ...this.kitMetrics, ...grafanaTimeSeries }
    },
    hasNoMetrics() {
      return !this.hasTimeSeriesMetrics && !this.hasTableMetrics
    },
    grafanaMetrics(): Array<Record<GrafanaMetricId, GrafanaMetricData>> {
      return this.kitMetricsStore.metricsByChartType[KitChartType.Gauge]
    },
    kitMetrics(): Record<KitMetricId, EvercamMetricData> {
      return {
        [KitMetricId.SolarCharger]: {
          stats: this.kitMetricsStore.kitSolarChargerStats,
          type: KitChartType.Graph,
          chartProps: {
            yAxisConfig: [
              {
                title: "Voltage",
                targets: ["voltage"],
              },
              {
                title: "Current",
                targets: ["current"],
              },
            ],
          },
        },
        [KitMetricId.PanelPower]: {
          stats: this.kitMetricsStore.kitPanelPowerStats,
          type: KitChartType.Graph,
          chartProps: {
            yAxisConfig: [
              {
                title: "Voltage",
                targets: ["panelVoltage"],
              },
              {
                title: "Power",
                targets: ["panelPower"],
              },
            ],
          },
        },
      }
    },
  },
  methods: {
    async onMetricsPeriodChange(period: KitMetricPeriod) {
      this.kitMetricsStore.selectedMetricsPeriod = period
      await this.kitMetricsStore.fetchMetrics()
    },
  },
})
</script>

<style lang="scss">
.kit-metrics {
  flex: 1;
  overflow: auto;

  display: grid;
  place-items: start center;
}
</style>
